import { DialogDescription, DialogTitle } from '@radix-ui/react-dialog'
import { getTextCss } from '@taxbit-private/cosmic'
import { Command } from 'cmdk'
import { FormEventHandler } from 'react'
import styled from 'styled-components'

import { CommandGroupMenu, Separator } from './CommandGroupMenu'
import { MenuCommandGroup } from '../cmdkTypes'

type CommandMenuProps = {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  onInputChange: FormEventHandler<HTMLInputElement>
  filterCommands: (value: string, search: string) => number
  isLoading?: boolean
  commandOutput?: string
  commandGroups: MenuCommandGroup[]
}

const CommandMenu = ({
  isOpen,
  onOpenChange,
  onInputChange,
  filterCommands,
  isLoading,
  commandOutput,
  commandGroups,
}: CommandMenuProps) => {
  const dialogTitle = 'Command palette'
  return (
    <>
      {isOpen && <Overlay onClick={() => onOpenChange(false)} />}
      <Dialog
        title={dialogTitle}
        open={isOpen}
        onOpenChange={onOpenChange}
        filter={filterCommands}
        aria-label={dialogTitle}
        label={dialogTitle}
      >
        <VisuallyHidden as={DialogTitle}>{dialogTitle}</VisuallyHidden>
        <VisuallyHidden as={DialogDescription}>Search for commands</VisuallyHidden>
        <Input placeholder="Type a command or search" onChangeCapture={onInputChange} />

        <List>
          {isLoading ? (
            <>
              <Loading>Generating output...</Loading>
              <Separator />
            </>
          ) : (
            commandOutput
          )}

          {!commandOutput && <Empty>No results found.</Empty>}

          {commandGroups.map((group) => (
            <CommandGroupMenu key={group.name} {...group} />
          ))}
        </List>
      </Dialog>
    </>
  )
}

const Overlay = styled.div(
  ({ theme }) => `
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: ${theme.zIndex.backdrop};
`
)

const Dialog = styled(Command.Dialog)(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    position: fixed;
    left: 50%;
    top: ${theme.measure.s};
    transform: translateX(-50%);
    background-color: ${theme.color.white};
    box-shadow: ${theme.shadow};
    width: ${theme.measure.xxxl};
    max-height: ${theme.measure.xl};
    max-width: 100%;
    border-radius: ${theme.borderRadius.m};
    border: ${theme.borderWidth.s} solid ${theme.color.border};
    z-index: ${theme.zIndex.modal};
    overflow: hidden;
  `
)

const Input = styled(Command.Input)(
  ({ theme }) => `
  padding: ${theme.spacing.m};
  border: none;
  border-bottom: ${theme.borderWidth.s} solid ${theme.color.border};
  background-color: transparent;
  ${getTextCss(theme).body};

  &:focus-visible {
    outline: none;
  }
`
)

const List = styled(Command.List)(
  ({ theme }) => `
  [data-selected="true"] {
    background-color: ${theme.color.gray1};
  }

  flex-grow: 1;
  overflow-y: scroll;
`
)

const Empty = styled(Command.Empty)(
  ({ theme }) => `
  padding: ${theme.spacing.m};
  ${getTextCss(theme)['body-small']};
  color: ${theme.color.textSecondary};
`
)

const Loading = styled(Command.Loading)(
  ({ theme }) => `
  padding: ${theme.spacing.m};
`
)

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`

export default CommandMenu
